/**
 * cdn-domain-ssl-tls-tab.tsx — SSL / TLS tab for a CDN domain (act-as).
 *
 * Two sections:
 *   1. TLS settings — min version, HTTP/2, HTTP/3, auto-HTTPS, HSTS.
 *      GET/PUT /api/v3/cdn/domains/:id/tls
 *   2. Certificates — list (uploaded / ACME / Tiger), upload form, ACME issue,
 *      Tiger distribute, per-cert activate.
 *      GET/POST/DELETE /api/v3/cdn/domains/:id/certs
 *      POST /api/v3/cdn/domains/:id/certs/:id/activate
 *      POST /api/v3/cdn/domains/:id/certs/acme
 *      POST /api/v3/cdn/domains/:id/certs/tiger
 *
 * All mutations route through ActAsMutationConfirm.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Upload, RefreshCw, Star, Trash2, CheckCircle2 } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Skeleton,
  Badge,
  Switch,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNTls,
  useActAsCDNCerts,
  actAsCDNRulesKeys,
  type TlsConfig,
  type Certificate,
  type CertType,
} from '@/api/act-as-cdn-rules';
import { actAsClient } from '@/api/act-as-client';
import { formatDate } from '@/lib/format';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

/* ── TLS settings form ───────────────────────────────────────────────────── */

interface TlsForm {
  min_version: '1.2' | '1.3';
  http2: boolean;
  http3: boolean;
  auto_https: boolean;
  hsts_enabled: boolean;
  hsts_max_age: string;
  hsts_subdomains: boolean;
  hsts_preload: boolean;
}

const TLS_DEFAULTS: TlsForm = {
  min_version: '1.2',
  http2: true,
  http3: false,
  auto_https: true,
  hsts_enabled: false,
  hsts_max_age: '31536000',
  hsts_subdomains: false,
  hsts_preload: false,
};

function hydrateTls(cfg: TlsConfig): TlsForm {
  return {
    min_version: cfg.min_version ?? '1.2',
    http2: cfg.http2 ?? true,
    http3: cfg.http3 ?? false,
    auto_https: cfg.auto_https ?? true,
    hsts_enabled: cfg.hsts_enabled ?? false,
    hsts_max_age: String(cfg.hsts_max_age ?? 31536000),
    hsts_subdomains: cfg.hsts_subdomains ?? false,
    hsts_preload: cfg.hsts_preload ?? false,
  };
}

function toTlsPayload(f: TlsForm): TlsConfig {
  return {
    min_version: f.min_version,
    http2: f.http2,
    http3: f.http3,
    auto_https: f.auto_https,
    hsts_enabled: f.hsts_enabled,
    hsts_max_age: Number(f.hsts_max_age) || 0,
    hsts_subdomains: f.hsts_subdomains,
    hsts_preload: f.hsts_preload,
  };
}

/* ── TLS settings card ───────────────────────────────────────────────────── */

function TlsSettingsCard({ domainId }: { domainId: string }) {
  const { t } = useTranslation();
  const q = useActAsCDNTls(domainId);

  const [form, setForm] = useState<TlsForm>(TLS_DEFAULTS);
  const [snapshot, setSnapshot] = useState<TlsForm>(TLS_DEFAULTS);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    const next = q.data ? hydrateTls(q.data) : TLS_DEFAULTS;
    setForm(next);
    setSnapshot(next);
  }, [q.data]);

  const set = <K extends keyof TlsForm>(k: K, v: TlsForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const dirty = JSON.stringify(form) !== JSON.stringify(snapshot);

  if (q.isLoading) return <Skeleton style={{ height: 280 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.ssl.tls_load_error', 'Failed to load TLS settings')}
        onRetry={() => q.refetch()}
      />
    );
  }

  const payload = toTlsPayload(form);
  const isConfigured = !!q.data;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('cdn_domain.ssl.tls_card_title', 'TLS settings')}</CardTitle>
          <CardDescription>
            {t('cdn_domain.ssl.tls_card_desc', 'Protocol versions, HTTP/2, HTTP/3, and HSTS policy.')}
          </CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          {/* Min TLS version */}
          <div style={{ padding: 'var(--vn-space-3) 0', display: 'grid', gap: 'var(--vn-space-2)' }}>
            <Field label={t('cdn_domain.ssl.field_min_version', 'Minimum TLS version')}>
              {(props) => (
                <Select
                  value={form.min_version}
                  onValueChange={(v) => set('min_version', v as TlsForm['min_version'])}
                >
                  <SelectTrigger {...props} style={{ maxWidth: 180 }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1.2">TLS 1.2</SelectItem>
                    <SelectItem value="1.3">TLS 1.3 only</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>

          <SettingRow
            title={t('cdn_domain.ssl.field_http2', 'HTTP/2')}
            description={t('cdn_domain.ssl.field_http2_desc', 'Enable HTTP/2 between client and edge.')}
            control={<Switch checked={form.http2} onCheckedChange={(v) => set('http2', v)} />}
          />
          <SettingRow
            title={t('cdn_domain.ssl.field_http3', 'HTTP/3 (QUIC)')}
            description={t('cdn_domain.ssl.field_http3_desc', 'Enable HTTP/3 (QUIC) for supported clients.')}
            control={<Switch checked={form.http3} onCheckedChange={(v) => set('http3', v)} />}
          />
          <SettingRow
            title={t('cdn_domain.ssl.field_auto_https', 'Automatic HTTPS redirect')}
            description={t('cdn_domain.ssl.field_auto_https_desc', 'Redirect all HTTP requests to HTTPS at the edge.')}
            control={<Switch checked={form.auto_https} onCheckedChange={(v) => set('auto_https', v)} />}
          />

          {/* HSTS */}
          <SettingRow
            title={t('cdn_domain.ssl.field_hsts', 'HSTS')}
            description={t('cdn_domain.ssl.field_hsts_desc', 'Add Strict-Transport-Security response header.')}
            control={<Switch checked={form.hsts_enabled} onCheckedChange={(v) => set('hsts_enabled', v)} />}
          />
          {form.hsts_enabled && (
            <div
              style={{
                display: 'grid',
                gap: 'var(--vn-space-4)',
                marginTop: 'var(--vn-space-3)',
                paddingLeft: 'var(--vn-space-4)',
                borderLeft: '2px solid var(--vn-border-subtle)',
              }}
            >
              <Field
                label={t('cdn_domain.ssl.field_hsts_max_age', 'Max-Age (seconds)')}
                description={t('cdn_domain.ssl.field_hsts_max_age_desc', '31536000 = 1 year (recommended).')}
              >
                {(props) => (
                  <NumericInput
                    {...props}
                    value={form.hsts_max_age}
                    onChange={(e) => set('hsts_max_age', e.target.value)}
                    style={{ maxWidth: 160 }}
                  />
                )}
              </Field>
              <SettingRow
                title={t('cdn_domain.ssl.field_hsts_subdomains', 'Include subdomains')}
                control={
                  <Switch checked={form.hsts_subdomains} onCheckedChange={(v) => set('hsts_subdomains', v)} />
                }
              />
              <SettingRow
                title={t('cdn_domain.ssl.field_hsts_preload', 'Preload')}
                description={t('cdn_domain.ssl.field_hsts_preload_desc', 'Request inclusion in browser preload lists.')}
                control={
                  <Switch checked={form.hsts_preload} onCheckedChange={(v) => set('hsts_preload', v)} />
                }
              />
            </div>
          )}
        </CardBody>
      </Card>

      <ActionBar
        start={
          isConfigured ? (
            <Button variant="danger" onClick={() => setDeleteConfirmOpen(true)}>
              {t('cdn_domain.ssl.tls_action_remove', 'Remove TLS config')}
            </Button>
          ) : undefined
        }
        end={
          <>
            {isConfigured && (
              <Button variant="secondary" disabled={!dirty} onClick={() => setForm(snapshot)}>
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
            <Button disabled={isConfigured && !dirty} onClick={() => setConfirmOpen(true)}>
              {isConfigured
                ? t('cdn_domain.ssl.tls_action_save', 'Save TLS settings')
                : t('cdn_domain.ssl.tls_action_create', 'Create TLS config')}
            </Button>
          </>
        }
      />

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={
          isConfigured
            ? t('cdn_domain.ssl.confirm_save_title', 'Save TLS settings')
            : t('cdn_domain.ssl.confirm_create_title', 'Create TLS configuration')
        }
        summary={t(
          'cdn_domain.ssl.confirm_save_summary',
          'TLS min version {{version}}, HTTP/2: {{http2}}, auto-HTTPS: {{auto}}, HSTS: {{hsts}}.',
          {
            version: form.min_version,
            http2: form.http2 ? 'on' : 'off',
            auto: form.auto_https ? 'on' : 'off',
            hsts: form.hsts_enabled ? 'on' : 'off',
          },
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.tls(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/tls`, payload)}
        onSuccess={() => setSnapshot(form)}
      />

      <ActAsMutationConfirm
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('cdn_domain.ssl.confirm_delete_tls_title', 'Remove TLS configuration')}
        summary={t(
          'cdn_domain.ssl.confirm_delete_tls_summary',
          'TLS settings will be removed. The edge will fall back to provider defaults.',
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.tls(domainId)]}
        mutationFn={() => actAsClient.delete(`/cdn/domains/${domainId}/tls`)}
        onSuccess={() => { setForm(TLS_DEFAULTS); setSnapshot(TLS_DEFAULTS); }}
        destructive
        confirmLabel={t('cdn_domain.ssl.tls_action_remove', 'Remove TLS config')}
      />
    </>
  );
}

/* ── Cert type badge ─────────────────────────────────────────────────────── */

function CertTypeBadge({ type }: { type: CertType }) {
  const labels: Record<CertType, string> = {
    uploaded: 'Uploaded',
    acme: 'ACME / Let\'s Encrypt',
    tiger: 'Tiger (managed)',
  };
  const tones: Record<CertType, 'info' | 'success' | 'warning'> = {
    uploaded: 'info',
    acme: 'success',
    tiger: 'warning',
  };
  return (
    <Badge tone={tones[type]} appearance="subtle" size="sm">
      {labels[type]}
    </Badge>
  );
}

/* ── Certificate row ─────────────────────────────────────────────────────── */

interface CertRowProps {
  cert: Certificate;
  domainId: string;
  onActivated: () => void;
  onDeleted: () => void;
}

function CertRow({ cert, domainId, onActivated, onDeleted }: CertRowProps) {
  const { t } = useTranslation();
  const [activateOpen, setActivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const expiresLabel = formatDate(cert.expires_at);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        gap: 'var(--vn-space-3)',
        alignItems: 'center',
        padding: 'var(--vn-space-3)',
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-subtle)',
      }}
    >
      {/* Active indicator */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {cert.active ? (
          <CheckCircle2 size={16} style={{ color: 'var(--vn-success-text)' }} />
        ) : (
          <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid var(--vn-border-default)' }} />
        )}
      </div>

      {/* Cert info */}
      <div style={{ display: 'grid', gap: 'var(--vn-space-1)', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
          <span
            style={{
              fontWeight: 500,
              fontSize: 'var(--vn-font-size-sm)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cert.common_name ?? cert.id}
          </span>
          <CertTypeBadge type={cert.type} />
          {cert.active && (
            <StatusPill tone="success">
              {t('cdn_domain.ssl.cert_active', 'Active')}
            </StatusPill>
          )}
        </div>
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          {cert.issuer ? `${cert.issuer} · ` : ''}
          {t('cdn_domain.ssl.cert_expires', 'Expires:')} {expiresLabel}
          {cert.san && cert.san.length > 0 && ` · SAN: ${cert.san.slice(0, 3).join(', ')}${cert.san.length > 3 ? '…' : ''}`}
        </span>
      </div>

      {/* Activate */}
      {!cert.active && (
        <Button variant="secondary" size="sm" onClick={() => setActivateOpen(true)}>
          {t('cdn_domain.ssl.cert_activate', 'Activate')}
        </Button>
      )}

      {/* Delete */}
      <IconButton
        variant="ghost"
        size="sm"
        aria-label={t('common.delete', 'Delete')}
        onClick={() => setDeleteOpen(true)}
      >
        <Trash2 size={14} />
      </IconButton>

      <ActAsMutationConfirm
        open={activateOpen}
        onOpenChange={setActivateOpen}
        title={t('cdn_domain.ssl.confirm_activate_title', 'Activate certificate')}
        summary={t(
          'cdn_domain.ssl.confirm_activate_summary',
          'Certificate {{cn}} will become the active TLS certificate for this domain.',
          { cn: cert.common_name ?? cert.id },
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.certs(domainId)]}
        mutationFn={() => actAsClient.post(`/cdn/domains/${domainId}/certs/${cert.id}/activate`)}
        onSuccess={onActivated}
        confirmLabel={t('cdn_domain.ssl.cert_activate', 'Activate')}
      />

      <ActAsMutationConfirm
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('cdn_domain.ssl.confirm_delete_cert_title', 'Delete certificate')}
        summary={t(
          'cdn_domain.ssl.confirm_delete_cert_summary',
          'Certificate {{cn}} will be permanently deleted.',
          { cn: cert.common_name ?? cert.id },
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.certs(domainId)]}
        mutationFn={() => actAsClient.delete(`/cdn/domains/${domainId}/certs/${cert.id}`)}
        onSuccess={onDeleted}
        destructive
        confirmLabel={t('common.delete', 'Delete')}
      />
    </div>
  );
}

/* ── Upload form ─────────────────────────────────────────────────────────── */

interface UploadFormProps {
  domainId: string;
  onUploaded: () => void;
}

function UploadForm({ domainId, onUploaded }: UploadFormProps) {
  const { t } = useTranslation();
  const [certPem, setCertPem] = useState('');
  const [keyPem, setKeyPem] = useState('');
  const [chainPem, setChainPem] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const canUpload = certPem.trim().length > 0 && keyPem.trim().length > 0;

  if (!expanded) {
    return (
      <Button
        variant="secondary"
        size="sm"
        startIcon={<Upload size={14} />}
        onClick={() => setExpanded(true)}
      >
        {t('cdn_domain.ssl.upload_cert_btn', 'Upload certificate')}
      </Button>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('cdn_domain.ssl.upload_form_title', 'Upload PEM certificate')}</CardTitle>
        <CardDescription>
          {t('cdn_domain.ssl.upload_form_desc', 'Paste PEM-encoded certificate and private key. Chain is optional.')}
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Field label={t('cdn_domain.ssl.field_cert_pem', 'Certificate (PEM)')}>
          {(props) => (
            <Textarea
              {...props}
              value={certPem}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              rows={6}
              style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-xs)' }}
              onChange={(e) => setCertPem(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('cdn_domain.ssl.field_key_pem', 'Private key (PEM)')}>
          {(props) => (
            <Textarea
              {...props}
              value={keyPem}
              placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
              rows={6}
              style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-xs)' }}
              onChange={(e) => setKeyPem(e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('cdn_domain.ssl.field_chain_pem', 'Certificate chain (optional)')}
        >
          {(props) => (
            <Textarea
              {...props}
              value={chainPem}
              placeholder="-----BEGIN CERTIFICATE-----&#10;(intermediate)&#10;-----END CERTIFICATE-----"
              rows={4}
              style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-xs)' }}
              onChange={(e) => setChainPem(e.target.value)}
            />
          )}
        </Field>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
          <Button disabled={!canUpload} onClick={() => setConfirmOpen(true)}>
            {t('cdn_domain.ssl.upload_action', 'Upload')}
          </Button>
          <Button variant="secondary" onClick={() => { setExpanded(false); setCertPem(''); setKeyPem(''); setChainPem(''); }}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </div>
      </CardBody>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.ssl.confirm_upload_title', 'Upload certificate')}
        summary={t('cdn_domain.ssl.confirm_upload_summary', 'A new PEM certificate will be uploaded for this domain.')}
        queryKeysToInvalidate={[actAsCDNRulesKeys.certs(domainId)]}
        mutationFn={() =>
          actAsClient.post(`/cdn/domains/${domainId}/certs`, {
            cert_pem: certPem,
            key_pem: keyPem,
            ...(chainPem.trim() ? { chain_pem: chainPem } : {}),
          })
        }
        onSuccess={() => {
          setCertPem(''); setKeyPem(''); setChainPem(''); setExpanded(false);
          onUploaded();
        }}
        confirmLabel={t('cdn_domain.ssl.upload_action', 'Upload')}
      />
    </Card>
  );
}

/* ── Certificates card ───────────────────────────────────────────────────── */

function CertificatesCard({ domainId }: { domainId: string }) {
  const { t } = useTranslation();
  const certsQ = useActAsCDNCerts(domainId);

  const [acmeOpen, setAcmeOpen] = useState(false);
  const [tigerOpen, setTigerOpen] = useState(false);

  if (certsQ.isLoading) return <Skeleton style={{ height: 160 }} />;
  if (certsQ.isError) {
    return (
      <TabError
        title={t('cdn_domain.ssl.certs_load_error', 'Failed to load certificates')}
        onRetry={() => certsQ.refetch()}
      />
    );
  }

  const certs = certsQ.data ?? [];

  return (
    <Card>
      <CardHeader
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<RefreshCw size={14} />}
              onClick={() => setAcmeOpen(true)}
            >
              {t('cdn_domain.ssl.acme_btn', 'Issue ACME cert')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Star size={14} />}
              onClick={() => setTigerOpen(true)}
            >
              {t('cdn_domain.ssl.tiger_btn', 'Distribute Tiger cert')}
            </Button>
          </div>
        }
      >
        <CardTitle>{t('cdn_domain.ssl.certs_card_title', 'Certificates')}</CardTitle>
        <CardDescription>
          {t('cdn_domain.ssl.certs_card_desc', 'Manage TLS certificates. Activate one to use it for HTTPS traffic.')}
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {certs.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<ShieldCheck size={20} />}
            title={t('cdn_domain.ssl.certs_empty_title', 'No certificates yet')}
            description={t(
              'cdn_domain.ssl.certs_empty_desc',
              'Upload a PEM certificate, issue a free ACME cert, or distribute a managed Tiger cert.',
            )}
          />
        ) : (
          certs.map((cert) => (
            <CertRow
              key={cert.id}
              cert={cert}
              domainId={domainId}
              onActivated={() => void certsQ.refetch()}
              onDeleted={() => void certsQ.refetch()}
            />
          ))
        )}

        {/* Upload form (inline toggle) */}
        <UploadForm
          domainId={domainId}
          onUploaded={() => void certsQ.refetch()}
        />
      </CardBody>

      {/* ACME issue confirm */}
      <ActAsMutationConfirm
        open={acmeOpen}
        onOpenChange={setAcmeOpen}
        title={t('cdn_domain.ssl.confirm_acme_title', 'Issue ACME certificate')}
        summary={t(
          'cdn_domain.ssl.confirm_acme_summary',
          'A free Let\'s Encrypt certificate will be issued via ACME for this domain. DNS must be pointed to the CDN CNAME.',
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.certs(domainId)]}
        mutationFn={() => actAsClient.post(`/cdn/domains/${domainId}/certs/acme`)}
        onSuccess={() => void certsQ.refetch()}
        confirmLabel={t('cdn_domain.ssl.acme_btn', 'Issue ACME cert')}
      />

      {/* Tiger distribute confirm */}
      <ActAsMutationConfirm
        open={tigerOpen}
        onOpenChange={setTigerOpen}
        title={t('cdn_domain.ssl.confirm_tiger_title', 'Distribute Tiger certificate')}
        summary={t(
          'cdn_domain.ssl.confirm_tiger_summary',
          'A managed SSL certificate from the Tiger inventory will be distributed to this domain.',
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.certs(domainId)]}
        mutationFn={() => actAsClient.post(`/cdn/domains/${domainId}/certs/tiger`)}
        onSuccess={() => void certsQ.refetch()}
        confirmLabel={t('cdn_domain.ssl.tiger_btn', 'Distribute Tiger cert')}
      />
    </Card>
  );
}

/* ── Tab component ───────────────────────────────────────────────────────── */

interface CdnDomainSslTlsTabProps {
  domainId: string;
}

export function CdnDomainSslTlsTab({ domainId }: CdnDomainSslTlsTabProps) {
  const { t } = useTranslation();

  return (
    <Section
      title={t('cdn_domain.ssl.title', 'SSL / TLS')}
      description={t(
        'cdn_domain.ssl.description',
        'Configure TLS protocol settings and manage certificates for this domain.',
      )}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-6)' }}>
        <TlsSettingsCard domainId={domainId} />
        <CertificatesCard domainId={domainId} />
      </div>
    </Section>
  );
}
